import React, { useEffect, useState } from 'react';
import {
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	FormControl,
	IconButton,
	InputAdornment,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Paper,
	Skeleton,
	TablePagination,
} from '@mui/material';
import { Refresh, Search, Add, Visibility } from '@mui/icons-material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MainGrid from '../../components/MainGrid';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
} from '../../Utils/AuthUtil';
import {
	fetchAllExternalElder,
	fetchResidents,
} from '../../services/ElderService';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { dark, surface, teal } from '../../assets/theme/Colors';

const rows = [
	{
		name: 'Margaret Wilson',
		initials: 'MW',
		avatar: '#d32f2f',
		room: '14',
		careType: 'Dementia',
		source: 'API',
		plan: 'Overdue 14d',
		lastObs: '2 hrs ago',
		risk: 'High',
		riskColor: 'red',
		planColor: 'red',
	},
	{
		name: 'James Patterson',
		initials: 'JP',
		avatar: '#ef6c00',
		room: '7',
		careType: 'Nursing',
		source: 'API',
		plan: 'Due soon',
		lastObs: '5 hrs ago',
		risk: 'Medium',
		riskColor: 'amber',
		planColor: 'amber',
	},
	{
		name: 'Eleanor Hughes',
		initials: 'EH',
		avatar: '#00897b',
		room: '3',
		careType: 'Residential',
		source: 'Manual',
		plan: 'Current',
		lastObs: 'Today 9am',
		risk: 'Low',
		riskColor: 'green',
		planColor: 'green',
	},
	{
		name: 'Robert Thompson',
		initials: 'RT',
		avatar: '#1976d2',
		room: '11',
		careType: 'Nursing',
		source: 'API',
		plan: 'Due soon',
		lastObs: 'Yesterday',
		risk: 'Medium',
		riskColor: 'amber',
		planColor: 'amber',
	},
	{
		name: 'Susan Mitchell',
		initials: 'SM',
		avatar: '#7b1fa2',
		room: '6',
		careType: 'Residential',
		source: 'Manual',
		plan: 'Current',
		lastObs: 'Today 8am',
		risk: 'Low',
		riskColor: 'green',
		planColor: 'green',
	},
];

const colors = [
	'#2563eb', // blue
	'#dc2626', // red
	'#00a89d', // green
	'#ca8a04', // yellow
	'#7c3aed', // purple
	'#ea580c', // orange
	'#0891b2', // cyan
	'#db2777', // pink
];

function Tag({ label, color }) {
	const getTagColor = color => {
		switch (color) {
			case 'red':
				return { bgcolor: '#ffe5e5', color: '#c62828' };
			case 'amber':
				return { bgcolor: '#fff4e5', color: '#ef6c00' };
			case 'green':
				return { bgcolor: '#e6f7ea', color: '#2e7d32' };
			case 'blue':
				return { bgcolor: '#e6f0ff', color: '#1565c0' };
			case 'gray':
				return { bgcolor: '#f2f2f2', color: '#616161' };
			default:
				return {};
		}
	};
	return (
		<Chip
			label={label}
			size="small"
			sx={{
				height: 22,
				fontSize: 11,
				fontWeight: 600,
				borderRadius: '999px',
				...getTagColor(color),
			}}
		/>
	);
}

export default function AllResidentProfile() {
	const navigate = useNavigate();
	const location = useLocation();
	const { residentId: residentIdFromUrl } = useParams();
	const { state } = location;
	const userId = getUserId();
	const homeId = getElderHomeId();
	const token = getAccessToken();
	const [careType, setCareType] = useState('All care types');
	const [residents, setResidents] = useState([]);
	const [residentsData, setResidentsData] = useState([]);
	const [searchText, setSearchedText] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [page, setPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const selectedResidentId = state?.selectedResidentId || residentIdFromUrl;

	useEffect(() => {
		fetchResident();
	}, []);

	useEffect(() => {
		if (!selectedResidentId || residents.length === 0) {
			return;
		}

		const selectedIndex = residents.findIndex(
			resident => String(resident.id) === String(selectedResidentId),
		);

		if (selectedIndex >= 0) {
			setPage(Math.floor(selectedIndex / rowsPerPage));
		}
	}, [selectedResidentId, residents, rowsPerPage]);

	async function fetchResident() {
		setIsLoading(true);

		const response = await fetchResidents(homeId, token);
		const exResponse = await fetchAllExternalElder(homeId, token);

		const formatResidents = (data, isExternal) => {
			const filteredData = !isExternal
				? data
				: data.filter(item => item.reviewed);

			return filteredData.map((item, index) => {
				const initials = item.name
					?.split(' ')
					.map(word => word[0])
					.join('')
					.toUpperCase()
					.slice(0, 2);

				const avatarColor = colors[index % colors.length];

				return {
					id: item.id,
					elderId: item.elderId,
					name: item.name,
					initials,
					avatar: avatarColor,
					room:
						item.elderHistory?.roomNumber ||
						item.elderHistory?.RoomNumber ||
						'N/A',

					careType: item.elderHistory?.careType || 'N/A',
					source: isExternal ? 'API' : 'Manual',
					plan: 'Current',
					lastObs: 'N/A',
					risk: 'Low',
					riskColor: 'green',
					planColor: 'green',
					isExternal,
					createdDate: item.createdDate,
				};
			});
		};

		let allResidents = [];

		if (response.message === 'Success') {
			allResidents = [...formatResidents(response.content || [], false)];
		}

		if (exResponse.message === 'Success') {
			allResidents = [
				...allResidents,
				...formatResidents(exResponse.content || [], true),
			];
		}

		const sortedResidents = [...allResidents].sort((a, b) => {
			const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
			const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;

			return dateB - dateA; // newest first
		});

		setResidents(sortedResidents);
		setResidentsData(sortedResidents);
		setIsLoading(false);
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	function onSearch(search) {
		setSearchedText(search);
		const searchText = search.toLowerCase();

		if (search === '') {
			setResidents(residentsData);
		} else {
			const searchResults = residentsData.filter(value => {
				return (
					value.elderId.toLowerCase().includes(searchText) ||
					value.name.toLowerCase().includes(searchText)
				);
			});

			if (searchResults.length > 0) {
				setResidents(searchResults);
			} else {
				setResidents([]);
			}
		}
	}

	return (
		<MainGrid>
			<Box sx={{ width: '100%' }}>
				{/* HEADER */}
				<Stack
					direction={{ xs: 'column', lg: 'row' }}
					justifyContent="space-between"
					alignItems={{ lg: 'center' }}
					spacing={2}
					mb={3}>
					<Box>
						<Typography
							variant="h4"
							sx={{
								fontSize: '22px',
								fontWeight: 600,
								color: dark,
								mb: 0.5,
							}}>
							All Resident Profiles
						</Typography>

						<Typography
							sx={{
								fontSize: 14,
								color: '#6b8888',
							}}>
							{residents.length} residents — API onboarded and
							manually added
						</Typography>
					</Box>

					{/* ACTIONS */}
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						spacing={1.5}
						alignItems={{ md: 'center' }}>
						{/* SEARCH */}

						<TextField
							size="small"
							placeholder="Search resident..."
							onChange={e => onSearch(e.target.value)}
							InputProps={{
								sx: {
									height: 30,
									fontSize: 12,
									backgroundColor: '#FFFFFF',
									'& input': {
										padding: '4px 8px',
									},
								},
								startAdornment: (
									<InputAdornment position="start">
										<Search
											sx={{
												color: '#6b8888',
												fontSize: 20,
											}}
										/>
									</InputAdornment>
								),
							}}
						/>

						{/* FILTER */}
						{/* <FormControl size="small" sx={{ minWidth: 160 }}>
							<Select
								value={careType}
								onChange={e => setCareType(e.target.value)}
								sx={{
									background: '#fff',
									borderRadius: 2,
								}}>
								<MenuItem value="All care types">
									All care types
								</MenuItem>

								<MenuItem value="Dementia">Dementia</MenuItem>

								<MenuItem value="Nursing">Nursing</MenuItem>

								<MenuItem value="Residential">
									Residential
								</MenuItem>
							</Select>
						</FormControl> */}

						{/* BUTTON */}
						<Button
							variant="contained"
							onClick={() => navigate('/addResident')}
							startIcon={<PersonAddIcon />}
							sx={{
								background: teal,
								color: dark,
								fontWeight: 600,
								borderRadius: 2,
								height: 30,
								fontSize: 12,
								textTransform: 'none',
								px: 2,
								'&:hover': {
									background: '#00b0a0',
								},
							}}>
							Onboard
						</Button>
					</Stack>
				</Stack>

				{/* TABLE CARD */}
				<Card
					elevation={0}
					sx={{ width: '100%', border: '1px solid #d8e8e8' }}>
					<TableContainer>
						<Table sx={{ borderRadius: 5 }}>
							<TableHead>
								<TableRow>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Resident
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Room
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Care Type
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Source
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Plan Status
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Last Obs
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Risk
									</TableCell>
									<TableCell
										sx={{
											fontSize: 12,
											color: '#6b8888',
											fontWeight: 600,
										}}>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{isLoading ? (
									[...Array(3)].map((_, mIndex) => (
										<TableRow key={mIndex}>
											{[...Array(8)].map((_, index) => (
												<TableCell
													key={index}
													align="center">
													<Skeleton
														animation="wave"
														style={{
															borderRadius: '5px',
															height: 20,
														}}
														variant="rounded"
													/>
												</TableCell>
											))}
										</TableRow>
									))
								) : residents.length > 0 ? (
									residents
										.slice(
											page * rowsPerPage,
											page * rowsPerPage + rowsPerPage,
										)
										.map((r, i) => (
											<TableRow
												key={i}
												hover
												sx={{
													cursor: 'pointer',
												}}
												onClick={() =>
													console.log(
														'open profile',
														r.name,
													)
												}>
												<TableCell>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<Avatar
															sx={{
																width: 28,
																height: 28,
																fontSize: 12,
																bgcolor:
																	r.avatar,
															}}>
															{r.initials}
														</Avatar>

														<Typography
															fontSize={12}>
															{r.name}
														</Typography>
													</Stack>
												</TableCell>

												<TableCell
													sx={{ fontSize: 12 }}>
													{r.room}
												</TableCell>

												<TableCell
													sx={{ fontSize: 12 }}>
													{r.careType}
												</TableCell>

												<TableCell>
													<Tag
														label={r.source}
														color={
															r.source === 'API'
																? 'blue'
																: 'gray'
														}
													/>
												</TableCell>

												<TableCell>
													<Tag
														label={r.plan}
														color={r.planColor}
													/>
												</TableCell>

												<TableCell
													sx={{ fontSize: 12 }}>
													{r.lastObs}
												</TableCell>

												<TableCell>
													<Tag
														label={r.risk}
														color={r.riskColor}
													/>
												</TableCell>

												<TableCell>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<IconButton
															size="small"
															onClick={() => {
																navigate(
																	`/resident/${r.id}?elderId=${
																		r.elderId
																	}&external=${
																		r.isExternal
																			? '1'
																			: '0'
																	}`,
																	{
																		state: {
																			id: r.id,
																			elderId:
																				r.elderId,
																			isExternalElder:
																				r.isExternal,
																			from: location.pathname,
																		},
																	},
																);
															}}
															sx={{
																width: 34,
																height: 22,
																border: '1px solid #d8e8e8',
																borderRadius:
																	'8px',
																backgroundColor:
																	'#fff',
																color: '#1a2424',
																'&:hover': {
																	backgroundColor:
																		surface,
																	borderColor:
																		teal,
																	color: dark,
																},
															}}>
															<Visibility fontSize="small" />
														</IconButton>

														{/* {r.source === 'API' && (
															<IconButton
																size="small"
																onClick={e => {
																	e.stopPropagation();
																	console.log(
																		'optimize',
																	);
																}}
																sx={{
																	width: 34,
																	height: 22,
																	borderRadius:
																		'8px',
																	backgroundColor:
																		teal,
																	color: dark,
																	'&:hover': {
																		backgroundColor:
																			'#00b0a0',
																	},
																}}>
																<Refresh fontSize="small" />
															</IconButton>
														)} */}
													</Stack>
												</TableCell>
											</TableRow>
										))
								) : (
									<TableRow>
										<TableCell colSpan={8} align="center">
											<Typography fontSize={13}>
												No data available
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[]}
						component="div"
						count={residents ? residents.length : 0}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Card>
			</Box>
		</MainGrid>
	);
}
